import "./IndividualForm.scss";
import { useCallback, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import FormikInput from "components/FormikInput/FormikInput";
import FormCheckbox from "components/FormCheckbox/FormCheckbox";
import CustomButton from "components/Button/Button";
import { useAppSelector, useAppDispatch } from "store";
import { exchangeActions } from "../../store/reducers/exchange";
import {
  individualBuyCryptoForm,
  individualSellCryptoForm,
} from "store/reducers/exchange";
import { OTCtype } from "models/otc";
import { scrollToSection } from "utils/scroll";
import { useTranslation, Trans } from "react-i18next";
import i18next from "../../i18n/config";
interface IndividualFormProps {
  backToCalculator: () => void;
}
const validationSchema = (isCryptoToFiat: boolean) => {
  return Yup.object().shape({
    senderName: Yup.string().required(
      i18next.t("exchange.formContent.incorrect")
    ),
    senderLastName: Yup.string().required(
      i18next.t("exchange.formContent.incorrect")
    ),
    senderwalletAddress: isCryptoToFiat
      ? Yup.string().required(i18next.t("exchange.formContent.incorrect"))
      : Yup.string().notRequired(),
    recipientName: Yup.string().required(
      i18next.t("exchange.formContent.incorrect")
    ),
    recipientLastName: Yup.string().required(
      i18next.t("exchange.formContent.incorrect")
    ),
    recipientWallet: isCryptoToFiat
      ? Yup.string().notRequired()
      : Yup.string().required(i18next.t("exchange.formContent.incorrect")),
    recipientIBAN: isCryptoToFiat
      ? Yup.string().required(i18next.t("exchange.formContent.incorrect"))
      : Yup.string().notRequired(),
    recipientresidentialAddress: isCryptoToFiat
      ? Yup.string().required(i18next.t("exchange.formContent.incorrect"))
      : Yup.string().notRequired(),
    politicallyExposed: Yup.boolean(),
    termsAccepted: Yup.boolean().oneOf(
      [true],
      i18next.t("exchange.formContent.mustAccept")
    ),
  });
};

const IndividualForm = ({ backToCalculator }: IndividualFormProps) => {
  const { from } = useAppSelector((state) => state.exchange);
  const [isNoPep, setIsNoPep] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const isCryptoToFiat = from.type === "CRYPTO";
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const openPEPPopup = useCallback(() => {
    dispatch(exchangeActions.setIsOpenPEPPopUp(true));
  }, [dispatch]);
  return (
    <Formik
      initialValues={{
        senderName: "",
        senderLastName: "",
        senderwalletAddress: "",
        recipientName: "",
        recipientLastName: "",
        recipientWallet: "",
        recipientIBAN: "",
        recipientresidentialAddress: "",
        politicallyExposed: false,
        termsAccepted: false,
        createPersonalAccount: false,
      }}
      validationSchema={() => validationSchema(isCryptoToFiat)}
      validateOnMount
      onSubmit={async (values, { setErrors, validateForm }) => {
        const errors = await validateForm();

        if (Object.keys(errors).length > 0) {
          return;
        } else if (!isTermsAccepted) {
          return;
        }
        scrollToSection("home");
        if (isNoPep) {
          let response;
          if (isCryptoToFiat) {
            response = await dispatch(
              individualSellCryptoForm({
                sender: {
                  firstName: values.senderName,
                  lastName: values.senderLastName,
                  walletAddress: values.senderwalletAddress,
                  type: OTCtype.CRYPTO_TO_FIAT_INDIVIDUAL,
                },
                recipient: {
                  firstName: values.recipientName,
                  lastName: values.recipientLastName,
                  IBAN: values.recipientIBAN,
                  residentialAddress: values.recipientresidentialAddress,
                  type: OTCtype.CRYPTO_TO_FIAT_INDIVIDUAL,
                },
                createAccount: true,
              })
            );
          } else {
            response = await dispatch(
              individualBuyCryptoForm({
                sender: {
                  firstName: values.senderName,
                  lastName: values.senderLastName,
                  type: OTCtype.FIAT_TO_CRYPTO_INDIVIDUAL,
                },
                recipient: {
                  firstName: values.recipientName,
                  lastName: values.recipientLastName,
                  walletAddress: values.recipientWallet,
                  type: OTCtype.FIAT_TO_CRYPTO_INDIVIDUAL,
                },
                createAccount: true,
              })
            );
          }
          if (individualBuyCryptoForm.rejected.match(response)) {
            if (
              response.payload?.find((error) => error.field === "walletAddress")
            ) {
              setErrors({
                recipientWallet: t("exchange.formContent.incorrectWallet"),
              });
            }
          }
          if (individualSellCryptoForm.rejected.match(response)) {
            if (
              response.payload?.find((error) => error.field === "walletAddress")
            ) {
              setErrors({
                senderwalletAddress: t("exchange.formContent.incorrectWallet"),
              });
            }
          }
        } else {
          openPEPPopup();
          return;
        }
      }}
    >
      {({
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        values,
      }) => {
        return (
          <Form className="individual-form p-4 max-w-lg mx-auto border rounded-lg shadow-lg">
            <div className="mb-4">
              <h2 className="text-lg font-bold form-title">
                {t("exchange.formContent.sender")}
              </h2>
              <div className="name-row">
                <FormikInput
                  name="senderName"
                  id="filled-basic"
                  label={t("exchange.formContent.name")}
                  variant="filled"
                  value={values.senderName}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("senderName", e.target.value.trim());
                  }}
                  touched={touched.senderName}
                  error={errors.senderName}
                />
                <FormikInput
                  name="senderLastName"
                  id="filled-basic"
                  label={t("exchange.formContent.lastName")}
                  variant="filled"
                  value={values.senderLastName}
                  touched={touched.senderLastName}
                  error={errors.senderLastName}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("senderLastName", e.target.value.trim());
                  }}
                />
              </div>
              {isCryptoToFiat && (
                <FormikInput
                  name="senderwalletAddress"
                  id="filled-basic"
                  label={t("exchange.formContent.wallet")}
                  variant="filled"
                  value={values.senderwalletAddress}
                  touched={touched.senderwalletAddress}
                  error={errors.senderwalletAddress}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("senderwalletAddress", e.target.value.trim());
                  }}
                />
              )}
              <div className="mb-4 checkbox-section pep-section">
                <FormCheckbox
                  checked={isNoPep}
                  name="politicallyExposed"
                  onChange={(e) => {
                    setIsNoPep(!isNoPep);
                    handleChange(e);
                  }}
                />
                <div className="checkbox-title">
                  <Trans
                    i18nKey="exchange.formContent.PEP"
                    components={{
                      pep: (
                        <a
                          href="/Politically Exposed Person (PEP) ENG.pdf"
                          target="_blank"
                        />
                      ),
                    }}
                  />
                  <span className="asterisk">*</span>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <h2 className="text-lg font-bold  form-title">
                {t("exchange.formContent.recipient")}
              </h2>
              <div className="name-row">
                <FormikInput
                  name="recipientName"
                  id="filled-basic"
                  label={t("exchange.formContent.name")}
                  variant="filled"
                  touched={touched.recipientName}
                  error={errors.recipientName}
                  value={values.recipientName}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("recipientName", e.target.value.trim());
                  }}
                />
                <FormikInput
                  name="recipientLastName"
                  id="filled-basic"
                  label={t("exchange.formContent.lastName")}
                  variant="filled"
                  touched={touched.recipientLastName}
                  error={errors.recipientLastName}
                  value={values.recipientLastName}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("recipientLastName", e.target.value.trim());
                  }}
                />
              </div>
              {isCryptoToFiat ? (
                <>
                  <FormikInput
                    name="recipientIBAN"
                    id="filled-basic"
                    label={t("exchange.formContent.IBAN")}
                    variant="filled"
                    touched={touched.recipientIBAN}
                    error={errors.recipientIBAN}
                    value={values.recipientIBAN}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldValue("recipientIBAN", e.target.value.trim());
                    }}
                  />
                  <FormikInput
                    name="recipientresidentialAddress"
                    id="filled-basic"
                    label={t("exchange.formContent.residentialAddress")}
                    variant="filled"
                    touched={touched.recipientresidentialAddress}
                    error={errors.recipientresidentialAddress}
                    value={values.recipientresidentialAddress}
                    onChange={handleChange}
                    onBlur={(e) => {
                      handleBlur(e);
                      setFieldValue(
                        "recipientresidentialAddress",
                        e.target.value.trim()
                      );
                    }}
                  />
                </>
              ) : (
                <FormikInput
                  name="recipientWallet"
                  id="filled-basic"
                  label={t("exchange.formContent.wallet")}
                  variant="filled"
                  touched={touched.recipientWallet}
                  error={errors.recipientWallet}
                  value={values.recipientWallet}
                  onChange={handleChange}
                  onBlur={(e) => {
                    handleBlur(e);
                    setFieldValue("recipientWallet", e.target.value.trim());
                  }}
                />
              )}
            </div>
            <div className="mb-4 checkbox-section">
              <FormCheckbox
                checked={isTermsAccepted}
                onChange={(e) => {
                  setIsTermsAccepted(!isTermsAccepted);
                  handleChange(e);
                }}
                name="termsAccepted"
              />
              <div className="checkbox-title">
                <Trans
                  i18nKey="exchange.formContent.agreementText"
                  components={{
                    privacy: <a href="/Privacy_Policy.pdf" target="_blank" />,
                    terms: <a href="/Terms_of_Service.pdf" target="_blank" />,
                    aml: <a href="/AML_CFT_Policy.pdf" target="_blank" />,
                  }}
                />
                <span className="asterisk">*</span>
              </div>
            </div>
            <CustomButton
              type="submit"
              btnName={t("buttons.continue")}
              className="blue-btn"
            />
            <CustomButton
              btnName={t("buttons.back")}
              type="button"
              className="border-btn"
              onClick={backToCalculator}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default IndividualForm;
